import { DragEvent, ReactNode, useCallback, useRef, useState } from "react";
import "./PiDropRegion.css";

interface PiDropRegionProps {
  children: ReactNode;
  /** Files dropped anywhere in the region. Validation belongs to the caller. */
  onFiles: (files: FileList) => void;
  /** Shown on the overlay while a drag is over the region. */
  label: string;
  className?: string;
  dataTestId?: string;
  /**
   * Refuse drops. The region still lights up on a drag so the user gets an
   * answer, but shows `disabledLabel` and discards the files rather than
   * accepting a drop that has nowhere to go.
   */
  disabled?: boolean;
  /** Shown instead of `label` while `disabled`. Say why, not just no. */
  disabledLabel?: string;
}

/**
 * Makes a whole scrolling region accept dropped files.
 *
 * The upload zone used to be the only drop target, which meant that once you
 * had scrolled past it there was nowhere to drop at all — the drag just
 * failed, silently. Anchoring the zone would have spent a third of the results
 * area permanently to fix that; accepting the drop anywhere costs nothing and
 * works at any scroll position, in either view.
 *
 * This owns dragging outright: the zone inside it no longer handles drags, so
 * there is only ever one active state to get stuck.
 */
export default function PiDropRegion(props: PiDropRegionProps) {
  const {
    children,
    onFiles,
    label,
    className,
    dataTestId,
    disabled,
    disabledLabel,
  } = props;
  const [isActive, setIsActive] = useState(false);
  // dragenter/dragleave fire for every child crossed on the way through, so a
  // boolean flickers. Depth counts enters against leaves instead.
  const depth = useRef(0);

  // Only external file drags. The editor drags its own nodes around with
  // HTML5 DnD, and those must not light this up.
  const carriesFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer?.types ?? []).includes("Files");

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    setIsActive(true);
  }, []);

  const onDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!carriesFiles(event)) return;
      // Without this the browser refuses the drop and animates the file back.
      event.preventDefault();

      // The overlay cannot set the cursor: it is pointer-events: none, and a
      // drag takes its cursor from dropEffect regardless. This is what turns
      // the pointer into the "no drop" one over a region that will refuse.
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = disabled ? "none" : "copy";
      }
    },
    [disabled]
  );

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    depth.current = Math.max(depth.current - 1, 0);
    if (depth.current === 0) setIsActive(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      depth.current = 0;
      setIsActive(false);

      const files = event.dataTransfer?.files;
      // Discard rather than accept: the caller has told us there is nowhere
      // for these to go, and the overlay has already said so.
      if (disabled) return;
      if (files && files.length > 0) onFiles(files);
    },
    [disabled, onFiles]
  );

  return (
    <div
      className={["pidropregion-root", className].filter(Boolean).join(" ")}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={dataTestId}
    >
      {children}

      {isActive && (
        <div
          className={[
            "pidropregion-overlay",
            disabled && "pidropregion-overlay-disabled",
          ]
            .filter(Boolean)
            .join(" ")}
          data-testid="drop-region-overlay"
          data-disabled={disabled ? "true" : undefined}
        >
          <div className="pidropregion-label">
            {disabled ? disabledLabel ?? label : label}
          </div>
        </div>
      )}
    </div>
  );
}
